import { DialogTitle, Icon } from '@aibees/shared/components';
import { classnames, fNumber } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { InformNotificationPopup } from '../inform-notification-popup';
import * as Styled from './styles';
import * as SX from './sx';
import type { EnrichDialogProps as Props } from './types';

export const EnrichDialog = memo(
  ({
    className,
    onClose,
    open,
    icon,
    title,
    informations,
    cost,
    enrichLoading,
    countLoading,
    onSubmit,
    recordsCount,
    disableEnrich,
    unEnrichableHelperText,
    description,
    submitButtonText,
    costText,
  }: Props) => {
    return (
      <Styled.Wrapper
        className={classnames('EnrichDialog', className)}
        sx={SX.root}
        maxWidth="xs"
        fullWidth
        open={open}
        onClose={onClose}
      >
        <Box>
          <DialogTitle title={title} icon={icon} onClose={onClose} />

          <DialogContent>
            <Typography color="grey.700" variant="body2" fontWeight={500}>
              You selected{' '}
              <Box component="span" color="grey.700" fontWeight="bold">
                {fNumber(recordsCount)}
              </Box>{' '}
              records.
            </Typography>
            <Styled.List>
              {informations.map((item) => (
                <Styled.ListItem key={item.label}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography
                      variant="body2"
                      component="span"
                      color="grey.600"
                    >
                      {countLoading ? (
                        <CircularProgress size="12px" sx={SX.progress} />
                      ) : (
                        <Typography
                          component="span"
                          variant="body2"
                          color="grey.600"
                          fontWeight="bold"
                        >
                          {fNumber(
                            item.count !== null && item.count !== undefined
                              ? item.count
                              : 0
                          )}{' '}
                        </Typography>
                      )}

                      {item.label}
                    </Typography>
                    {item.helperText && (
                      <Box component="span" sx={SX.popoverIcon}>
                        <InformNotificationPopup>
                          {item.helperText}
                        </InformNotificationPopup>
                      </Box>
                    )}
                  </Stack>
                </Styled.ListItem>
              ))}
            </Styled.List>
            {description}
            <Box sx={SX.costBox}>
              <Icon name="coin" color="primary.500" size={18} />
              <Typography
                color="primary.500"
                variant="body3"
                fontWeight="light"
              >
                This action will cost{' '}
                <Typography
                  color="primary.500"
                  variant="body3"
                  fontWeight="bold"
                  component="span"
                >
                  {cost}x
                </Typography>{' '}
                credits per {costText ? costText : 'record'}.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              variant="text"
              color="default"
              size="small"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Tooltip title={disableEnrich ? unEnrichableHelperText : undefined}>
              <Box>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={onSubmit}
                  loading={enrichLoading || countLoading}
                  disabled={disableEnrich}
                >
                  {submitButtonText || 'Enrich'}
                </LoadingButton>
              </Box>
            </Tooltip>
          </DialogActions>
        </Box>
      </Styled.Wrapper>
    );
  }
);

EnrichDialog.displayName = 'EnrichDialog';
